import {
  Button,
  Col,
  Icon,
  type IconName,
  ListEmpty,
  PageBody,
  Row,
  Tappable,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { WhenLabel } from "../localization/when-label.tsx";
import { read } from "../net/rally.ts";
import { AppEnvironmentForm, type EnvironmentDraft } from "./app-environment-form.tsx";
import { AppSecrets } from "./app-secrets.tsx";

type Place = "rally-machine" | "external-managed" | "external-registered";
type Health = "unknown" | "healthy" | "unhealthy";

type Environment = {
  id: string;
  name: string;
  branch: string | null;
  place: Place;
  machine: string | null;
  provider: string | null;
  url: string | null;
  healthPath: string | null;
  recipe: EnvironmentDraft["recipe"];
  secrets: number;
  last: { url: string | null; health: Health; commit: string | null; at: string } | null;
};

type Standing = {
  app: string;
  slug: string;
  name: string;
  repositories: Array<{ name: string; url: string; token: string | null }>;
  environments: Environment[];
  sharedSecrets: number;
};

const HEALTH_IS: Record<Health, { glyph: IconName; tone: "accent" | "danger" | "text.tertiary" }> = {
  healthy: { glyph: "circle-check", tone: "accent" },
  unhealthy: { glyph: "circle-alert", tone: "danger" },
  unknown: { glyph: "circle-help", tone: "text.tertiary" },
};

function Fact({ name, said }: { name: string; said: string }) {
  return (
    <Row gap={2} align="center">
      <Text typeName="caption" color="text.tertiary" style={{ width: 92 }}>
        {name}
      </Text>
      <Text typeName="caption" color="text.secondary" numberOfLines={1} style={{ flex: 1 }}>
        {said}
      </Text>
    </Row>
  );
}

function shortRepository(url: string): string {
  return url
    .replace(/^https?:\/\//, "")
    .replace(/\.git$/, "")
    .replace(/^github\.com\//, "");
}

export function AppStanding({ app }: { app: string }) {
  const theme = useTheme();
  const [standing, setStanding] = useState<Standing | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<EnvironmentDraft | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(
    (current: () => boolean = () => true) => {
      setFailed(false);
      read("app.standing", { app })
        .then((said) => {
          if (current()) setStanding(said as Standing);
        })
        .catch(() => {
          if (current()) setFailed(true);
        });
    },
    [app],
  );

  const again = useCallback(() => load(), [load]);

  useEffect(() => {
    let stale = false;
    load(() => !stale);
    return () => {
      stale = true;
    };
  }, [load]);

  if (failed) return <ListEmpty line={say("app.loadFailed")} />;
  if (!standing) return <ListEmpty line={say("app.loading")} />;

  return (
    <PageBody testID="app-screen">
      <Col gap={1}>
        <Text typeName="title" color="text.primary">
          {standing.name}
        </Text>
        <Text typeName="caption" color="text.tertiary">
          {standing.slug}
        </Text>
      </Col>

      <Col gap={2} testID="app-environments">
        <Row gap={2} align="center">
          <Text typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
            {say("app.environments")}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            icon="plus"
            onPress={() => {
              setEditing(null);
              setAdding(true);
            }}
            testID="add-environment"
          >
            {say("app.addEnvironment")}
          </Button>
        </Row>
        {adding ? (
          <Col
            style={{
              padding: theme.space[3],
              borderWidth: 1,
              borderColor: theme.color["border.default"],
              borderRadius: theme.radius.lg,
            }}
          >
            <AppEnvironmentForm
              app={app}
              onCancel={() => setAdding(false)}
              onDone={() => {
                setAdding(false);
                again();
              }}
            />
          </Col>
        ) : null}
        {standing.environments.length === 0 ? (
          <Text typeName="body" color="text.tertiary">
            {say("app.noEnvironments")}
          </Text>
        ) : (
          standing.environments.map((place) => {
            const health = HEALTH_IS[place.last?.health ?? "unknown"];
            const where =
              place.place === "rally-machine"
                ? (place.machine ?? "")
                : (place.provider ?? say(`app.place.${place.place}` as "app.place.external-registered"));
            return (
              <Col
                key={place.id}
                testID={`app-environment-${place.name}`}
                gap={2}
                style={{
                  padding: theme.space[3],
                  borderWidth: 1,
                  borderColor: theme.color["border.default"],
                  borderRadius: theme.radius.lg,
                }}
              >
                <Row gap={2} align="center">
                  <Icon name={health.glyph} size={16} color={health.tone} />
                  <Text typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
                    {place.name}
                  </Text>
                  <Text typeName="caption" color="text.tertiary">
                    {say(`app.place.${place.place}` as "app.place.rally-machine")}
                  </Text>
                  <Tappable
                    accessibilityRole="button"
                    accessibilityLabel={say("app.editEnvironment")}
                    testID={`edit-environment-${place.name}`}
                    onPress={() => {
                      setAdding(false);
                      setEditing(editing?.name === place.name ? null : place);
                    }}
                    style={{ padding: theme.space[1] }}
                  >
                    <Icon name="pencil" size={14} color="text.tertiary" />
                  </Tappable>
                </Row>
                <Fact name={say("app.address")} said={place.url ?? place.last?.url ?? "—"} />
                <Fact
                  name={place.place === "rally-machine" ? say("app.machine") : say("app.provider")}
                  said={where || "—"}
                />
                <Fact name={say("app.branch")} said={place.branch ?? "—"} />
                {place.recipe.install ? (
                  <Fact name={say("app.installCommand")} said={place.recipe.install} />
                ) : null}
                {place.recipe.build ? (
                  <Fact name={say("app.buildCommand")} said={place.recipe.build} />
                ) : null}
                {place.recipe.start ? (
                  <Fact name={say("app.startCommand")} said={place.recipe.start} />
                ) : null}
                <Fact name={say("app.secretsHeld")} said={String(place.secrets)} />
                {place.last ? (
                  <Row gap={2} align="center">
                    <Text typeName="caption" color="text.tertiary" style={{ flex: 1 }}>
                      {say("app.lastRun")} {place.last.commit ? place.last.commit.slice(0, 7) : ""}
                    </Text>
                    <WhenLabel at={place.last.at} />
                  </Row>
                ) : (
                  <Text typeName="caption" color="text.tertiary">
                    {say("app.neverRun")}
                  </Text>
                )}
                <Text typeName="caption" color="text.tertiary">
                  {say("app.secretsHere")}
                </Text>
                <AppSecrets app={app} environment={place.name} onChanged={again} />
                {editing?.name === place.name ? (
                  <AppEnvironmentForm
                    app={app}
                    held={editing}
                    onCancel={() => setEditing(null)}
                    onDone={() => {
                      setEditing(null);
                      again();
                    }}
                  />
                ) : null}
              </Col>
            );
          })
        )}
      </Col>

      <Col gap={2} testID="app-repositories">
        <Text typeName="bodySemibold" color="text.primary">
          {say("app.repositories")}
        </Text>
        {standing.repositories.length === 0 ? (
          <Text typeName="body" color="text.tertiary">
            {say("app.noRepository")}
          </Text>
        ) : (
          standing.repositories.map((repo) => (
            <Fact
              key={repo.name}
              name={shortRepository(repo.url)}
              said={repo.token ? `${say("app.tokenHeld")} ${repo.token}` : say("app.noToken")}
            />
          ))
        )}
        <Text typeName="caption" color="text.tertiary">
          {say("app.secretsShared")}
        </Text>
        <AppSecrets app={app} environment={null} onChanged={again} />
      </Col>
    </PageBody>
  );
}
